import React, { Suspense, lazy } from 'react'
import JsonViewer from '../JsonViewer'
import LoadProject from './loaders/LoadProject'
import Paste from './loaders/Paste'
import UploadFile from './loaders/UploadFile'
import UrlFetch from './loaders/UrlFetch'
import Loading from './loading'
import styles from './DataSourcePicker.module.scss'

const SparqlFetch = lazy(() => import('./loaders/SparqlFetch'))

const SOURCES = [
  { id: 'upload', label: 'Upload', icon: 'upload' },
  { id: 'paste', label: 'Paste', icon: 'paste' },
  { id: 'url', label: 'URL & API', icon: 'link' },
  { id: 'sample', label: 'Sample library', icon: 'library' },
]

function SourceIcon({ name }) {
  const paths = {
    upload: <><path d="M12 16V3" /><path d="m7 8 5-5 5 5" /><path d="M4 14v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /></>,
    paste: <><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M16 5h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" /></>,
    link: <><path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 0 0-7.07-7.07L11.3 5.64" /><path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 0 0 7.07 7.07l1.17-1.17" /></>,
    library: <><path d="M5 4.5A2.5 2.5 0 0 1 7.5 2H20v18H7.5A2.5 2.5 0 0 1 5 17.5v-13Z" /><path d="M5 17.5A2.5 2.5 0 0 1 7.5 15H20" /></>,
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      {paths[name]}
    </svg>
  )
}

function SampleLibraryPanel({ onBrowseLibrary }) {
  return (
    <div className={styles.samplePanel}>
      <div>
        <h3>Start from a ready-made dataset</h3>
        <p>Explore curated datasets organised by the kind of chart they suit.</p>
      </div>
      <button type="button" className={styles.primaryButton} onClick={onBrowseLibrary}>
        Browse library
      </button>
    </div>
  )
}

/**
 * The empty-state source chooser is isolated from DataLoader so sources can be
 * reordered, added, or redesigned without touching parsing and data-grid logic.
 */
export default function DataSourcePicker({
  activeSource,
  onSourceChange,
  userInput,
  userData,
  userDataType,
  setUserInput,
  setLoadingError,
  setJsonData,
  initialOptionState,
  onProjectSelected,
  onBrowseLibrary,
}) {
  return (
    <section className={styles.picker} aria-label="Choose a data source">
      <LoadProject
        compact
        onProjectSelected={onProjectSelected}
        setLoadingError={setLoadingError}
      />

      <div className={styles.sourceTabs} role="tablist" aria-label="Data source">
        {SOURCES.map((source) => (
          <button
            key={source.id}
            type="button"
            role="tab"
            aria-selected={activeSource === source.id}
            className={`${styles.sourceTab} ${activeSource === source.id ? styles.sourceTabActive : ''}`}
            onClick={() => onSourceChange(source.id)}
          >
            <SourceIcon name={source.icon} />
            <span>{source.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.sourcePanel} role="tabpanel">
        {activeSource === 'upload' && (
          <UploadFile
            setUserInput={(rawInput) => setUserInput(rawInput, { type: 'upload' })}
            setLoadingError={setLoadingError}
          />
        )}

        {activeSource === 'paste' && (
          userDataType === 'json' && userData === null ? (
            <JsonViewer
              context={JSON.parse(userInput)}
              selectFilter={(ctx) => Array.isArray(ctx)}
              onSelect={(ctx, path) => setJsonData(ctx, path)}
            />
          ) : (
            <Paste
              userInput={userInput}
              setUserInput={(rawInput) => setUserInput(rawInput, { type: 'paste' })}
              setLoadingError={setLoadingError}
            />
          )
        )}

        {activeSource === 'url' && (
          <>
            <UrlFetch
              userInput={userInput}
              setUserInput={(rawInput, source) => setUserInput(rawInput, source)}
              setLoadingError={setLoadingError}
              initialState={initialOptionState?.type === 'url' ? initialOptionState : null}
            />
            <details className={styles.advanced}>
              <summary>Advanced: query a SPARQL endpoint</summary>
              <div className={styles.advancedBody}>
                <Suspense fallback={<Loading />}>
                  <SparqlFetch
                    userInput={userInput}
                    setUserInput={(rawInput, source) => setUserInput(rawInput, source)}
                    setLoadingError={setLoadingError}
                    initialState={initialOptionState?.type === 'sparql' ? initialOptionState : null}
                  />
                </Suspense>
              </div>
            </details>
          </>
        )}

        {activeSource === 'sample' && <SampleLibraryPanel onBrowseLibrary={onBrowseLibrary} />}
      </div>
    </section>
  )
}
